import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperPlaneRight, CircleNotch, Sparkle, UploadSimple, Briefcase, X } from '@phosphor-icons/react';
import { ChatMessage } from './ChatMessage';
import { CaseStudyCard } from './CaseStudyCard';
import { ExportChatButton } from './ExportChatButton';
import { chatWithPortfolio, generateSuggestedQuestions } from '../utils/chatService';
import { analyzeJobDescription } from '../utils/openaiService';
import { getCaseStudyThumbnail } from '../utils/caseStudyMetadata';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
const CHAT_STORAGE_KEY = 'taio_chat_messages';
const SESSION_KEY = 'taio_chat_session';
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const isNewSession = (): boolean => {
  const storedSessionId = sessionStorage.getItem(SESSION_KEY);
  if (!storedSessionId) {
    sessionStorage.setItem(SESSION_KEY, generateSessionId());
    return true;
  }
  return false;
};
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  caseStudyCards?: Array<{
    slug: string;
    title: string;
    relevance: number;
    description: string;
    thumbnail: string;
  }>;
}
interface ChatInterfaceProps {
  onCaseStudyClick: (slug: string) => void;
  isSplitView: boolean;
  activeCaseStudy?: string | null;
  onClose?: () => void;
}
const loadMessagesFromStorage = (): Message[] => {
  if (isNewSession()) {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    return getDefaultWelcomeMessage();
  }
  try {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
  } catch (error) {
    console.error('Error loading chat messages from storage:', error);
  }
  return getDefaultWelcomeMessage();
};
const getDefaultWelcomeMessage = (): Message[] => {
  return [{
    id: '1',
    role: 'assistant',
    content: "Hi! I'm TAIO, Mateo's AI portfolio assistant. Ask me anything about his work, skills, or experience. What would you like to know?",
    timestamp: new Date()
  }];
};
const saveMessagesToStorage = (messages: Message[]) => {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  } catch (error) {
    console.error('Error saving chat messages to storage:', error);
  }
};
export function ChatInterface({
  onCaseStudyClick,
  isSplitView,
  activeCaseStudy,
  onClose
}: ChatInterfaceProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    chatEntryPoint,
    setChatEntryPoint,
    closeChatDrawer,
    setPreviousPage
  } = usePortfolio();
  const [messages, setMessages] = useState<Message[]>(loadMessagesFromStorage);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRoleMatchMode, setIsRoleMatchMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  // Whether the user has engaged yet — determines if we show intro or chat list
  const hasUserMessages = messages.some(m => m.role === 'user');
  useEffect(() => {
    saveMessagesToStorage(messages);
  }, [messages]);
  const getCaseStudyTitle = (slug: string): string => {
    const titles: Record<string, string> = {
      'us-bank': 'US Bank Commercial Card',
      'cyber-security': 'Unisys Stealth',
      'ai-comparison': 'Watson vs Dialogflow',
      'case-western': 'Case Western Reserve University'
    };
    return titles[slug] || slug;
  };
  const caseStudyContext = activeCaseStudy ? getCaseStudyTitle(activeCaseStudy) : null;
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.id !== lastMessageId) {
      setLastMessageId(lastMessage.id);
      setTimeout(() => {
        if (!messagesContainerRef.current) return;
        if (lastMessage.role === 'user') {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          const messageElement = document.getElementById(`message-${lastMessage.id}`);
          if (messageElement && messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const messageTop = messageElement.offsetTop;
            const containerScrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            const messageBottom = messageTop + messageElement.clientHeight;
            const isVisible = messageTop >= containerScrollTop && messageBottom <= containerScrollTop + containerHeight;
            if (!isVisible) {
              container.scrollTo({
                top: messageTop - 20,
                behavior: 'smooth'
              });
            }
          }
        }
      }, 100);
    }
  }, [messages, lastMessageId]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        if (input) {
          setInput('');
        } else if (isRoleMatchMode) {
          setIsRoleMatchMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [input, isRoleMatchMode]);
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    if (chatEntryPoint && chatEntryPoint !== location.pathname) {
      navigate(chatEntryPoint);
    }
    setChatEntryPoint(null);
    closeChatDrawer();
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      if (isRoleMatchMode || input.length > 500) {
        const analysisResult = await analyzeJobDescription(input.trim());
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `I analyzed the job description for "${analysisResult.jobTitle}". Here's how Mateo's experience matches:\n\n${analysisResult.summary}`,
          timestamp: new Date(),
          caseStudyCards: analysisResult.relevantCaseStudies.map(cs => ({
            slug: cs.slug,
            title: cs.title,
            relevance: cs.relevance,
            description: cs.reason,
            thumbnail: getCaseStudyThumbnail(cs.slug)
          }))
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsRoleMatchMode(false);
      } else {
        const response = await chatWithPortfolio(input.trim(), messages, activeCaseStudy);
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          caseStudyCards: response.caseStudyCards
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "I'm having trouble processing that. Could you try rephrasing?",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleRoleMatchClick = () => {
    const instructionMessage: Message = {
      id: Date.now().toString(),
      role: 'assistant',
      content: "**Role Match Mode**\n\nPaste the job description into the chat, and I'll analyze how Mateo's skills and experience align with the role. I'll highlight relevant case studies and provide a detailed match assessment.",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, instructionMessage]);
    setIsRoleMatchMode(true);
  };
  const handleUploadJDClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    setIsRoleMatchMode(true);
    try {
      const text = await file.text();
      setInput(text);
      const analysisResult = await analyzeJobDescription(text);
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: `[Uploaded job description: ${file.name}]`,
        timestamp: new Date()
      };
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I analyzed the job description for "${analysisResult.jobTitle}". Here's how Mateo's experience matches:\n\n${analysisResult.summary}`,
        timestamp: new Date(),
        caseStudyCards: analysisResult.relevantCaseStudies.map(cs => ({
          slug: cs.slug,
          title: cs.title,
          relevance: cs.relevance,
          description: cs.reason,
          thumbnail: getCaseStudyThumbnail(cs.slug)
        }))
      };
      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setInput('');
      setIsRoleMatchMode(false);
    } catch (error) {
      console.error('File upload error:', error);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const handleCaseStudyCardClick = (slug: string) => {
    window.scrollTo(0, 0);
    setPreviousPage(location.pathname);
    onCaseStudyClick(slug);
  };
  return <div className={`flex flex-col h-full ${isSplitView ? 'hidden lg:flex' : 'flex'}`} role="region" aria-label="Chat interface" style={{
    position: 'relative',
    zIndex: 99999999
  }} data-id="element-145">
      {/* Mobile-Only Header with Close Button */}
      {onClose && <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/95 backdrop-blur-sm flex-shrink-0" data-id="element-146">
          <div className="flex items-center gap-2" data-id="element-147">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/35 to-blue-700/20 border border-blue-400/40 flex items-center justify-center flex-shrink-0" data-id="element-148">
              <Sparkle className="w-4 h-4 text-blue-300" weight="fill" aria-hidden="true" data-id="element-149" />
            </div>
            <h2 className="text-white font-medium text-base" data-id="element-150">Chat with TAIO</h2>
          </div>
          <button onClick={handleClose} className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/15 transition-colors border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Close chat" data-id="element-151">
            <X className="w-5 h-5 text-white" aria-hidden="true" data-id="element-152" />
          </button>
        </div>}

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 lg:px-4 py-4 lg:py-6 space-y-4 lg:space-y-6" style={{
      overflowAnchor: 'none',
      scrollBehavior: 'smooth',
      position: 'relative',
      zIndex: 1,
      WebkitOverflowScrolling: 'touch'
    }} role="log" aria-live="polite" aria-atomic="false" data-id="element-153">
        {!hasUserMessages /* Intro state — GPT-style centered landing */ ? <div className="h-full flex flex-col items-center justify-center px-6 py-12 text-center" data-id="element-154">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/35 to-blue-700/20 border border-blue-400/40 flex items-center justify-center mb-6" data-id="element-155">
              <Sparkle className="w-7 h-7 text-blue-300" weight="fill" aria-hidden="true" data-id="element-156" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-light text-white mb-3" style={{
          fontFamily: "'Gefika', sans-serif"
        }} data-id="element-157">
              How can I help?
            </h2>
            <p className="text-sm text-white/60 max-w-xs leading-relaxed" data-id="element-158">
              I'm TAIO, Mateo's portfolio assistant. Ask about his work, case
              studies, or how his experience maps to a role.
            </p>
          </div> : <>
            <AnimatePresence initial={false} data-id="element-159">
              {messages.map(message => <div key={message.id} id={`message-${message.id}`} style={{
            position: 'relative',
            zIndex: 1,
            scrollMarginTop: '20px'
          }} data-id="element-160">
                  <ChatMessage role={message.role} content={message.content} timestamp={message.timestamp} isStreaming={false} data-id="element-161" />

                  {/* Case Study Cards */}
                  {message.caseStudyCards && message.caseStudyCards.length > 0 && <div className="mt-4 ml-11 space-y-3" style={{
              position: 'relative',
              zIndex: 100,
              pointerEvents: 'auto'
            }} data-id="element-162">
                        {message.caseStudyCards.map(card => <CaseStudyCard key={card.slug} slug={card.slug} title={card.title} description={card.description} relevance={card.relevance} thumbnail={card.thumbnail} onClick={() => handleCaseStudyCardClick(card.slug)} data-id="element-163" />)}
                      </div>}
                </div>)}
            </AnimatePresence>

            {isLoading && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex flex-col items-start gap-1.5" data-id="element-164">
                {/* Typing header — mirrors ChatMessage header */}
                <div className="flex items-center gap-2.5" data-id="element-165">
                  <div className="relative flex-shrink-0" data-id="element-166">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-500/35 to-blue-700/20 border border-blue-400/40" data-id="element-167">
                      <Sparkle className="w-4 h-4 text-blue-300" weight="fill" aria-hidden="true" data-id="element-168" />
                    </div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-blue-400 border-2 border-black" aria-hidden="true" data-id="element-169" />
                  </div>
                  <div className="flex items-center gap-2 text-xs" data-id="element-170">
                    <span className="font-medium text-white/90" data-id="element-171">TAIO</span>
                    <span className="text-white/30" aria-hidden="true" data-id="element-172">
                      |
                    </span>
                    <span className="text-white/50" data-id="element-173">Typing...</span>
                  </div>
                </div>
                {/* Typing bubble */}
                <div className="pr-11" data-id="element-174">
                  <div className="bg-white/[0.08] border border-white/10 rounded-3xl rounded-bl-md px-5 py-3 flex items-center gap-1.5" data-id="element-175">
                    <motion.div className="w-1.5 h-1.5 bg-white/70 rounded-full" animate={{
                y: [0, -4, 0],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 0.9,
                repeat: Infinity,
                delay: 0
              }} data-id="element-176" />
                    <motion.div className="w-1.5 h-1.5 bg-white/70 rounded-full" animate={{
                y: [0, -4, 0],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 0.9,
                repeat: Infinity,
                delay: 0.15
              }} data-id="element-177" />
                    <motion.div className="w-1.5 h-1.5 bg-white/70 rounded-full" animate={{
                y: [0, -4, 0],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 0.9,
                repeat: Infinity,
                delay: 0.3
              }} data-id="element-178" />
                  </div>
                </div>
              </motion.div>}
          </>}

        <div ref={messagesEndRef} data-id="element-179" />
      </div>

      {/* Hidden file input for Upload JD */}
      <input ref={fileInputRef} type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" aria-label="Upload job description file" data-id="element-180" />

      {/* Input Area */}
      <div className="border-t border-white/10 p-3 lg:p-4 flex-shrink-0 bg-black/95 backdrop-blur-sm space-y-3" data-id="element-181">
        {/* Role Match + Upload JD — full-width side-by-side with tooltips */}
        <div className="grid grid-cols-2 gap-2" data-id="element-182">
          <div className="relative group" data-id="element-183">
            <button type="button" onClick={handleRoleMatchClick} className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/10 text-white/80 text-sm font-medium transition-colors flex items-center justify-center gap-2 min-h-[48px] focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Start role match mode" data-id="element-184">
              <Briefcase className="w-4 h-4 flex-shrink-0" weight="regular" aria-hidden="true" data-id="element-185" />
              <span data-id="element-186">Role Match</span>
            </button>
            {/* Hover tooltip */}
            <div role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs leading-snug rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 z-50 w-56 text-center" data-id="element-187">
              Paste a job description and I'll match Mateo's experience to the
              role.
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" aria-hidden="true" data-id="element-188" />
            </div>
          </div>

          <div className="relative group" data-id="element-189">
            <button type="button" onClick={handleUploadJDClick} className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/10 text-white/80 text-sm font-medium transition-colors flex items-center justify-center gap-2 min-h-[48px] focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Upload job description" data-id="element-190">
              <UploadSimple className="w-4 h-4 flex-shrink-0" weight="regular" aria-hidden="true" data-id="element-191" />
              <span data-id="element-192">Upload JD</span>
            </button>
            {/* Hover tooltip */}
            <div role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs leading-snug rounded-lg shadow-xl opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 z-50 w-56 text-center" data-id="element-193">
              Upload a .txt, .pdf, or .doc job description file for instant
              analysis.
              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" aria-hidden="true" data-id="element-194" />
            </div>
          </div>
        </div>

        {/* Chat input form */}
        <form onSubmit={handleSubmit} className="flex gap-2 items-end" data-id="element-195">
          <label htmlFor="chat-input" className="sr-only" data-id="element-196">
            Chat message input
          </label>
          <input id="chat-input" ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} placeholder={isRoleMatchMode ? 'Paste job description here...' : 'Message TAIO...'} disabled={isLoading} autoComplete="off" className="flex-1 bg-white/5 border border-white/15 rounded-2xl px-5 py-4 text-white placeholder-white/40 outline-none focus:border-white/30 focus:bg-white/[0.07] focus:ring-2 focus:ring-white/10 transition-colors disabled:opacity-50 text-base" style={{
          fontSize: '16px',
          minHeight: '52px'
        }} aria-label={isRoleMatchMode ? 'Paste job description here' : 'Message TAIO'} data-id="element-197" />
          <button type="submit" disabled={!input.trim() || isLoading} className="flex-shrink-0 w-[52px] h-[52px] bg-white hover:bg-white/90 active:bg-white/80 text-black rounded-2xl flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-white/40" aria-label={isLoading ? 'Sending message' : 'Send message'} data-id="element-198">
            {isLoading ? <CircleNotch className="w-5 h-5 animate-spin" aria-hidden="true" data-id="element-199" /> : <PaperPlaneRight className="w-5 h-5" weight="fill" aria-hidden="true" data-id="element-200" />}
          </button>
        </form>

        {/* Footer: keyboard hints + export */}
        <div className="flex items-center justify-between gap-3 text-xs text-white/40" data-id="element-201">
          <div className="hidden sm:flex items-center gap-3" aria-hidden="true" data-id="element-202">
            <span data-id="element-203">⌘K to focus</span>
            <span data-id="element-204">•</span>
            <span data-id="element-205">ESC to clear</span>
          </div>
          <div className="ml-auto" data-id="element-206">
            <ExportChatButton messages={messages} data-id="element-207" />
          </div>
        </div>
      </div>
    </div>;
}